import { computed, ref, watch } from 'vue'
import {
  buildSearchRequest,
  searchCommunes,
  suggestionToSelection,
  validateCommune,
  type CommuneSelection,
  type CommuneSuggestion,
  type CommuneValidationError,
} from '@/services/communeSearch'

/**
 * Single-field French commune autocomplete (postal code OR city).
 *
 * Holds the reactive UI state and orchestrates the pure logic in
 * `services/communeSearch`: debounced search, in-flight request cancellation
 * (AbortController), blur-delayed list hiding, and the selection lifecycle.
 *
 * The selection is the source of truth for "this value is a real commune":
 *  - picking a suggestion stores it;
 *  - editing the field afterwards invalidates it;
 *  - `validate()` re-checks a free-typed value against the API on submit.
 */
const DEBOUNCE_MS = 250
const TIMEOUT_MS = 5000
/** Delay before hiding the list on blur, so a suggestion click still registers. */
const BLUR_DELAY_MS = 150

export type { CommuneSuggestion, CommuneSelection, CommuneValidationError }

export function useCommuneAutocomplete() {
  const query = ref('')
  const suggestions = ref<CommuneSuggestion[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const open = ref(false)
  /** Set once a suggestion is picked (or a free value is validated). */
  const selected = ref<CommuneSelection | null>(null)

  let controller: AbortController | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let blurTimer: ReturnType<typeof setTimeout> | null = null
  /** True while we programmatically set `query` from a selection, so the watcher
   *  does not treat it as a manual edit (which would clear the selection). */
  let applyingSelection = false

  /** True when the user is typing but hasn't reached the minimum threshold yet. */
  const belowThreshold = computed(
    () => query.value.trim().length > 0 && buildSearchRequest(query.value) === null,
  )

  function cancelPending() {
    if (timer) { clearTimeout(timer); timer = null }
    if (controller) { controller.abort(); controller = null }
  }

  function reset() {
    cancelPending()
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null }
    suggestions.value = []
    loading.value = false
    error.value = null
    selected.value = null
    open.value = false
  }

  watch(query, (q) => {
    cancelPending()

    if (applyingSelection) {
      applyingSelection = false
      return
    }

    // Any manual edit invalidates a previously picked commune.
    selected.value = null
    error.value = null

    const request = buildSearchRequest(q)
    if (!request) {
      suggestions.value = []
      loading.value = false
      return
    }

    loading.value = true
    timer = setTimeout(async () => {
      controller = new AbortController()
      try {
        suggestions.value = await searchCommunes(q, {
          signal: controller.signal,
          timeoutMs: TIMEOUT_MS,
        })
      } catch (e) {
        // AbortError is expected when a newer query supersedes this one.
        if ((e as Error).name === 'AbortError') return
        suggestions.value = []
        error.value = (e as Error).message
      } finally {
        loading.value = false
      }
    }, DEBOUNCE_MS)
  })

  /** Write `query` programmatically without the watcher treating it as a manual
   *  edit. The flag is only armed when the value actually changes (otherwise the
   *  watcher wouldn't fire and the flag would leak onto the next real edit). */
  function setQuietly(value: string) {
    if (query.value === value) return
    applyingSelection = true
    query.value = value
  }

  /** Pick a suggestion: store it, reflect its label in the field, close the list. */
  function select(s: CommuneSuggestion) {
    setQuietly(s.label)
    selected.value = suggestionToSelection(s)
    suggestions.value = []
    open.value = false
  }

  /** Re-prime a previously stored selection (e.g. restored from persistence)
   *  without the field write being treated as a manual edit. */
  function restore(selection: CommuneSelection) {
    setQuietly(selection.label)
    selected.value = selection
  }

  function onFocus() {
    open.value = true
  }

  function onBlur() {
    if (blurTimer) clearTimeout(blurTimer)
    blurTimer = setTimeout(() => { open.value = false }, BLUR_DELAY_MS)
  }

  /**
   * Final check used on submit. Returns an error code (or `null` if valid).
   * A previously picked suggestion is trusted immediately; otherwise the
   * free-typed value is validated against the API.
   */
  async function validate(): Promise<CommuneValidationError | null> {
    if (selected.value) return null
    cancelPending()
    controller = new AbortController()
    loading.value = true
    try {
      const result = await validateCommune(query.value, {
        signal: controller.signal,
        timeoutMs: TIMEOUT_MS,
      })
      if (result.ok) {
        selected.value = result.selection
        setQuietly(result.selection.label)
        return null
      }
      return result.error
    } finally {
      loading.value = false
    }
  }

  return {
    query,
    suggestions,
    loading,
    error,
    open,
    selected,
    belowThreshold,
    reset,
    select,
    restore,
    onFocus,
    onBlur,
    validate,
  }
}
